import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, withText } from 'preact-i18n';
import { route } from 'preact-router';

import { loadPublicCalendarData, getCalendarColor, COURSE_COLOR } from '../../lib/calendarEventsLoader';

@withText(() => ({
    loadingText: <Text id='events.loading'>Loading events…</Text>,
    noEventsText: <Text id='events.no-upcoming-events'>No upcoming events</Text>,
    searchPlaceholder: <Text id='events.search-placeholder'>Search events…</Text>,
    coursesChipText: <Text id='events.courses-chip'>Courses</Text>,
}))
@observer
class EventsWidget extends Component {
    constructor(props) {
        super(props);
        this.state = {
            items: [],
            calendars: [],
            isLoading: true,
            error: null,
            selectedCalendars: Array.isArray(props.calendarIds) ? [...props.calendarIds] : [],
            markerCalendarIds: [], // hard scope from the marker — set after load
            showCourses: true,
            searchText: typeof props.searchText === 'string' ? props.searchText : '',
        };
    }

    loadData = async () => {
        const { customerId, calendarIds } = this.props;
        if (!customerId) {
            this.setState({ isLoading: false });
            return;
        }

        const { events, courseItems, calendars, error } = await loadPublicCalendarData(customerId);
        if (error) {
            this.setState({ isLoading: false, error });
            return;
        }

        const items = [...events, ...courseItems].sort((a, b) => new Date(a.start) - new Date(b.start));

        // Resolve marker tokens (which may be calendarIds OR calendar names) into actual calendarIds.
        // A single name token can match multiple calendars (e.g. real "Stevner" + synthetic "conventions" both named "Stevner").
        let selectedCalendars = this.state.selectedCalendars;
        if (Array.isArray(calendarIds) && calendarIds.length > 0) {
            const resolved = [];
            calendarIds.forEach(token => {
                const tokenStr = String(token);
                const idMatch = calendars.find(c => String(c.calendarId) === tokenStr);
                if (idMatch) {
                    resolved.push(idMatch.calendarId);
                    return;
                }
                const tokenLower = tokenStr.toLowerCase();
                const nameMatches = calendars.filter(c => (c.name || '').toLowerCase() === tokenLower);
                if (nameMatches.length > 0) {
                    nameMatches.forEach(c => resolved.push(c.calendarId));
                    return;
                }
                resolved.push(tokenStr);
            });
            selectedCalendars = [...new Set(resolved)];
        }

        const markerCalendarIds = (Array.isArray(calendarIds) && calendarIds.length > 0) ? selectedCalendars : [];
        this.setState({ items, calendars, isLoading: false, selectedCalendars, markerCalendarIds });
    }

    componentDidMount() {
        this.loadData();
    }

    toggleCalendar = (calendarId) => {
        const { selectedCalendars } = this.state;

        if (selectedCalendars.length === 0) {
            // Currently showing all — select only this one
            this.setState({ selectedCalendars: [calendarId] });
        } else if (selectedCalendars.includes(calendarId)) {
            if (selectedCalendars.length === 1) {
                // Only one selected — clicking it clears back to "all"
                this.setState({ selectedCalendars: [] });
            } else {
                // Multiple selected — remove this one
                this.setState({ selectedCalendars: selectedCalendars.filter(id => id !== calendarId) });
            }
        } else {
            // Not in selection — add it
            this.setState({ selectedCalendars: [...selectedCalendars, calendarId] });
        }
    }

    toggleCourses = () => {
        this.setState(prev => ({ showCourses: !prev.showCourses }));
    }

    onSearchChange = (e) => {
        this.setState({ searchText: e.target.value });
    }

    matchesSearch = (item, q) => {
        const haystack = `${item.title || ''} ${item.location || ''} ${item.courseTitle || ''}`.toLowerCase();
        const tokens = q.trim().toLowerCase().split(/\s+/).filter(t => t.length > 0);
        for (const tok of tokens) {
            if (tok.startsWith('-')) {
                const neg = tok.slice(1);
                if (neg && haystack.includes(neg)) return false;
            } else if (!haystack.includes(tok)) {
                return false;
            }
        }
        return true;
    }

    getItemColor = (item) => {
        if (item.isCourse) {
            return COURSE_COLOR;
        }
        return getCalendarColor(this.state.calendars, item.calendarId);
    }

    handleEventClick = (item) => {
        if (item.isConvention && item.conventionId) {
            route(`/convention/${item.conventionId}`);
        } else if (item.isCourse && item.courseId) {
            route(`/course/${item.courseId}`);
        }
    }

    formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('nb-NO', {
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    getLocalDateKey = (dateStr) => {
        const d = new Date(dateStr);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    groupByDay = (visibleItems) => {
        const groups = {};
        const order = [];

        visibleItems.forEach(item => {
            const key = this.getLocalDateKey(item.start);
            if (!groups[key]) {
                groups[key] = [];
                order.push(key);
            }
            groups[key].push(item);
        });

        return order.sort().map(key => ({ key, items: groups[key] }));
    }

    renderFilterBar = () => {
        const { searchPlaceholder, coursesChipText } = this.props;
        const { calendars, selectedCalendars, showCourses, searchText, items, markerCalendarIds } = this.state;
        const hasCourses = items.some(i => i.isCourse);
        const visibleCalendars = markerCalendarIds.length > 0
            ? calendars.filter(c => markerCalendarIds.includes(c.calendarId))
            : calendars;
        const showChips = visibleCalendars.length > 1 || hasCourses;

        return (
            <div class='mb-3'>
                <div class='input-group input-group-sm mb-2'>
                    <span class='input-group-text'>
                        <i class='fa-solid fa-magnifying-glass' />
                    </span>
                    <input
                        type='text'
                        class='form-control'
                        placeholder={searchPlaceholder}
                        value={searchText}
                        onInput={this.onSearchChange}
                    />
                </div>
                {showChips && (
                    <div class='d-flex flex-wrap gap-2'>
                        {visibleCalendars.map(cal => {
                            const color = getCalendarColor(calendars, cal.calendarId);
                            const isSelected = selectedCalendars.length === 0 || selectedCalendars.includes(cal.calendarId);
                            return (
                                <span
                                    key={cal.calendarId}
                                    class='badge px-2 py-1'
                                    style={`background-color: ${isSelected ? color : 'transparent'}; color: ${isSelected ? 'white' : color}; border: 2px solid ${color}; cursor: pointer; opacity: ${isSelected ? 1 : 0.6};`}
                                    onClick={() => this.toggleCalendar(cal.calendarId)}
                                >
                                    {cal.name}
                                </span>
                            );
                        })}
                        {hasCourses && (
                            <span
                                class='badge px-2 py-1'
                                style={`background-color: ${showCourses ? COURSE_COLOR : 'transparent'}; color: ${showCourses ? 'white' : COURSE_COLOR}; border: 2px solid ${COURSE_COLOR}; cursor: pointer; opacity: ${showCourses ? 1 : 0.6};`}
                                onClick={this.toggleCourses}
                            >
                                {coursesChipText}
                            </span>
                        )}
                    </div>
                )}
            </div>
        );
    }

    renderDateChip = (dateKey, firstItem) => {
        const d = new Date(dateKey);
        const dayNum = d.toLocaleDateString('nb-NO', { day: 'numeric' });
        const monthAbbr = d.toLocaleDateString('nb-NO', { month: 'short' });
        const color = this.getItemColor(firstItem);

        return (
            <div
                class='d-flex flex-column align-items-center justify-content-center rounded text-white flex-shrink-0'
                style={`width: 64px; background-color: ${color}; padding: 6px 4px;`}
            >
                <i class='fa-solid fa-calendar-day mb-1' style='font-size: 0.75em;' />
                <span style='font-size: 1.1em; font-weight: bold; line-height: 1;'>{dayNum}</span>
                <span style='font-size: 0.75em; text-transform: capitalize;'>{monthAbbr}</span>
            </div>
        );
    }

    renderEventCard = (item, idx) => {
        const color = this.getItemColor(item);
        const isClickable = (item.isConvention && item.conventionId) || (item.isCourse && item.courseId);

        return (
            <div
                key={item.id || item.dateMd5 || idx}
                class='card border-0 bg-body-secondary mb-2'
                style={`border-left: 4px solid ${color} !important;`}
                onClick={isClickable ? () => this.handleEventClick(item) : undefined}
            >
                <div class='card-body py-2 px-3' style={isClickable ? 'cursor: pointer;' : ''}>
                    <div class='d-flex justify-content-between align-items-start'>
                        <div class='flex-fill'>
                            <div class='fw-semibold small'>
                                {item.isCourse && (
                                    <i class='fa-solid fa-graduation-cap me-1' style={`color: ${COURSE_COLOR};`} />
                                )}
                                {this.formatTime(item.start)}
                                {item.end && ` – ${this.formatTime(item.end)}`}
                                {' · '}
                                {item.title}
                            </div>
                            {item.location && (
                                <div class='text-body-secondary small mt-1'>
                                    <i class='fa-solid fa-location-dot me-1' />
                                    {item.location}
                                </div>
                            )}
                            {!item.isCourse && item.body && (
                                <div class='text-body-secondary small mt-1' style='white-space: pre-wrap;'>
                                    {item.body}
                                </div>
                            )}
                            {item.isCourse && item.description && (
                                <div class='text-body-secondary small mt-1' style='white-space: pre-wrap; max-height: 60px; overflow: hidden;'>
                                    {item.description}
                                </div>
                            )}
                        </div>
                        {item.isCourse && (
                            <span class='badge ms-2' style={`background-color: ${COURSE_COLOR};`}>
                                <Text id='calendar.course'>Kurs</Text>
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { customerId, loadingText, noEventsText } = this.props;
        const { items, isLoading, error, selectedCalendars, showCourses, searchText } = this.state;

        if (!customerId) {
            return null;
        }

        if (isLoading) {
            return (
                <div class='p-3 text-center text-body-secondary'>
                    <i class='fa-duotone fa-spinner fa-spin fa-2x' />
                    <p class='mt-2'>{loadingText}</p>
                </div>
            );
        }

        if (error) {
            return (
                <div class='p-3 text-center text-danger'>
                    <i class='fa-duotone fa-triangle-exclamation fa-2x' />
                    <p class='mt-2'>{error}</p>
                </div>
            );
        }

        if (items.length === 0) {
            return (
                <div class='p-3 text-center text-body-secondary'>
                    <i class='fa-duotone fa-calendar-xmark fa-2x' />
                    <p class='mt-2'>{noEventsText}</p>
                </div>
            );
        }

        // Filter pipeline
        const { markerCalendarIds } = this.state;
        const visibleItems = items.filter(item => {
            // 0. Marker scope: when the marker specified calendars, never show events outside that scope.
            if (markerCalendarIds.length > 0 && !item.isCourse && !markerCalendarIds.includes(item.calendarId)) return false;
            // 1. Course toggle
            if (item.isCourse && !showCourses) return false;
            // 2. Calendar selection (non-courses only)
            if (!item.isCourse && selectedCalendars.length > 0 && !selectedCalendars.includes(item.calendarId)) return false;
            // 3. Search text
            if (searchText && !this.matchesSearch(item, searchText)) return false;
            return true;
        });

        const dayGroups = this.groupByDay(visibleItems);

        return (
            <div class='events-widget'>
                {this.renderFilterBar()}

                {visibleItems.length === 0 ? (
                    <p class='text-body-secondary fst-italic small'>
                        {searchText ? (
                            <Text id='events.no-results' fields={{ query: searchText }}>No matches for "{searchText}"</Text>
                        ) : (
                            <Text id='events.no-upcoming-events'>No upcoming events</Text>
                        )}
                    </p>
                ) : (
                    dayGroups.map(group => (
                        <div key={group.key} class='d-flex gap-3 mb-3'>
                            {this.renderDateChip(group.key, group.items[0])}
                            <div class='flex-fill'>
                                {group.items.map((item, idx) => this.renderEventCard(item, idx))}
                            </div>
                        </div>
                    ))
                )}
            </div>
        );
    }
}

export default EventsWidget;
